import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import BookingDetailContent from '../components/BookingDetailContent';
import Modal from '../components/Modal';
import RoomDayTimeline from '../components/RoomDayTimeline';
import Spinner from '../components/Spinner';
import { getRoomCalendar } from '../lib/api';
import {
  addIsoDays,
  availableIntervals,
  mergedBookedIntervals,
  OFFICE_END_MINUTES,
  OFFICE_START_MINUTES,
  timeFromMinutes,
  timelineStyle,
} from '../lib/roomCalendar';
import { errorMessage, formatDate, formatTime, getMalaysiaTodayISO } from '../lib/utils';
import { sortRoomsForDisplay } from '../lib/roomOrder';
import type { RoomCalendarBooking, RoomCalendarRoom } from '../types';

type CalendarView = 'day' | 'week';
type BookingDetail = { booking: RoomCalendarBooking; roomName: string };

const TIME_TICKS = Array.from(
  { length: Math.floor((OFFICE_END_MINUTES - OFFICE_START_MINUTES) / 60) + 1 },
  (_, index) => OFFICE_START_MINUTES + index * 60,
).concat(OFFICE_END_MINUTES);

function roomPlace(room: RoomCalendarRoom): string {
  return [room.floor_label, room.building].filter(Boolean).join(' · ');
}

function weekday(date: string, style: 'short' | 'long' = 'short'): string {
  return new Intl.DateTimeFormat('ms-MY', { weekday: style, timeZone: 'Asia/Kuala_Lumpur' })
    .format(new Date(`${date}T00:00:00+08:00`));
}

function intervalText(intervals: { start: number; end: number }[]): string {
  return intervals.length
    ? intervals.map((interval) => `${formatTime(timeFromMinutes(interval.start))}–${formatTime(timeFromMinutes(interval.end))}`).join(', ')
    : 'None';
}

function ScheduleSummary({ bookings, date }: { bookings: RoomCalendarBooking[]; date: string }) {
  return <div className="room-schedule-summary">
    <span><strong>Tempah</strong> {intervalText(mergedBookedIntervals(bookings, date))}</span>
    <span><strong>Tersedia</strong> {intervalText(availableIntervals(bookings, date))}</span>
  </div>;
}

function TimeHeader({ lead }: { lead: React.ReactNode }) {
  return <div className="room-calendar-time-row">
    <div className="room-calendar-sticky-cell room-calendar-lead">{lead}</div>
    <div className="room-calendar-time-header" aria-hidden="true">
      {TIME_TICKS.map((minutes, index) => <span
        key={minutes}
        className={index === 0 ? 'first' : index === TIME_TICKS.length - 1 ? 'last' : ''}
        style={{ left: timelineStyle(minutes, minutes).left }}
      >{formatTime(timeFromMinutes(minutes))}</span>)}
    </div>
  </div>;
}

function DayRoomRow({
  room,
  date,
  onBookingClick,
  onFreeClick,
}: {
  room: RoomCalendarRoom;
  date: string;
  onBookingClick: (booking: RoomCalendarBooking, roomName: string) => void;
  onFreeClick: (roomId: string, startMinutes: number) => void;
}) {
  return <div className="room-calendar-row">
    <div className="room-calendar-sticky-cell room-calendar-room-name">
      <strong>{room.name}</strong>
      <span>{roomPlace(room)} · {room.capacity} pax</span>
    </div>
    <div className="room-calendar-row-main">
      <RoomDayTimeline
        bookings={room.bookings}
        date={date}
        label={`${room.name} pada ${formatDate(date)}`}
        onBookingClick={(booking) => onBookingClick(booking, room.name)}
        onFreeClick={(start) => onFreeClick(room.room_id, start)}
      />
      <ScheduleSummary bookings={room.bookings} date={date} />
    </div>
  </div>;
}

function WeekDayRow({
  room,
  date,
  onBookingClick,
  onFreeClick,
}: {
  room: RoomCalendarRoom;
  date: string;
  onBookingClick: (booking: RoomCalendarBooking, roomName: string) => void;
  onFreeClick: (roomId: string, startMinutes: number) => void;
}) {
  return <div className="room-calendar-row">
    <div className="room-calendar-sticky-cell room-calendar-room-name week-day">
      <strong>{weekday(date, 'long')}</strong>
      <span>{formatDate(date)}</span>
    </div>
    <div className="room-calendar-row-main">
      <RoomDayTimeline
        bookings={room.bookings}
        date={date}
        label={`${room.name} pada ${formatDate(date)}`}
        onBookingClick={(booking) => onBookingClick(booking, room.name)}
        onFreeClick={(start) => onFreeClick(room.room_id, start)}
      />
      <ScheduleSummary bookings={room.bookings} date={date} />
    </div>
  </div>;
}

function BookingDetailModal({ detail, onClose }: { detail: BookingDetail; onClose: () => void }) {
  return <Modal title={detail.booking.title} onClose={onClose}>
    <BookingDetailContent booking={detail.booking} roomName={detail.roomName} showTitle={false} />
  </Modal>;
}

export default function RoomCalendarPage() {
  const [view, setView] = useState<CalendarView>('day');
  const [date, setDate] = useState(getMalaysiaTodayISO);
  const [rooms, setRooms] = useState<RoomCalendarRoom[]>([]);
  const [roomOptions, setRoomOptions] = useState<RoomCalendarRoom[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [query, setQuery] = useState('');
  const [place, setPlace] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [detail, setDetail] = useState<BookingDetail | null>(null);
  const navigate = useNavigate();
  const requestedRoomId = view === 'week' ? selectedRoomId : '';

  useEffect(() => {
    let cancelled = false;
    if (view === 'week' && !requestedRoomId) return () => { cancelled = true; };
    setStatus('loading');
    setLoadError('');
    void getRoomCalendar({
      startDate: date,
      dayCount: view === 'week' ? 7 : 1,
      roomId: view === 'week' ? requestedRoomId : null,
    }).then((result) => {
      if (cancelled) return;
      const orderedRooms = sortRoomsForDisplay(result);
      setRooms(orderedRooms);
      if (view === 'day') {
        setRoomOptions(orderedRooms);
        setSelectedRoomId((current) => orderedRooms.some((room) => room.room_id === current) ? current : orderedRooms[0]?.room_id ?? '');
      }
      setStatus('ready');
    }).catch((requestError) => {
      if (cancelled) return;
      setRooms([]);
      setLoadError(errorMessage(requestError));
      setStatus('error');
    });
    return () => { cancelled = true; };
  }, [date, refresh, requestedRoomId, view]);

  useEffect(() => {
    const refreshOnFocus = () => setRefresh((current) => current + 1);
    window.addEventListener('focus', refreshOnFocus);
    return () => window.removeEventListener('focus', refreshOnFocus);
  }, []);

  const places = useMemo(() => Array.from(new Set(roomOptions.map(roomPlace))), [roomOptions]);
  const filteredRooms = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return rooms.filter((room) => (!place || roomPlace(room) === place)
      && (!normalizedQuery || [room.name, room.building, room.floor_label, ...room.facilities]
        .filter(Boolean).join(' ').toLowerCase().includes(normalizedQuery)));
  }, [place, query, rooms]);

  const openCreateMeeting = (roomId: string, startMinutes: number, targetDate = date) => {
    const params = new URLSearchParams({ date: targetDate, room: roomId, start: timeFromMinutes(startMinutes) });
    navigate(`/meetings/new?${params.toString()}`);
  };

  const changeView = (nextView: CalendarView) => {
    if (nextView === 'week' && !selectedRoomId && roomOptions.length) setSelectedRoomId(roomOptions[0].room_id);
    setView(nextView);
  };

  const weekRoom = rooms[0] ?? null;

  return <Layout title="Kalendar Bilik">
    <section className="room-calendar-card">
      <div className="room-calendar-toolbar">
        <div className="calendar-view-switch" role="group" aria-label="Paparan kalendar">
          <button type="button" className={view === 'day' ? 'active' : ''} onClick={() => changeView('day')}>Hari</button>
          <button type="button" className={view === 'week' ? 'active' : ''} onClick={() => changeView('week')} disabled={!roomOptions.length}>Minggu</button>
        </div>
        <div className="calendar-date-controls">
          <button type="button" className="button button-ghost button-small" onClick={() => setDate(addIsoDays(date, view === 'week' ? -7 : -1))} aria-label={`${view === 'week' ? 'Minggu' : 'Hari'} sebelumnya`}>‹</button>
          <label className="field compact-field"><span>{view === 'week' ? 'Minggu bermula' : 'Tarikh'}</span><input type="date" value={date} onChange={(event) => { if (event.target.value) setDate(event.target.value); }} required /></label>
          <button type="button" className="button button-ghost button-small" onClick={() => setDate(addIsoDays(date, view === 'week' ? 7 : 1))} aria-label={`${view === 'week' ? 'Minggu' : 'Hari'} seterusnya`}>›</button>
          <button type="button" className="button button-secondary button-small" onClick={() => setDate(getMalaysiaTodayISO())}>Hari ini</button>
        </div>
        <button type="button" className="button button-ghost button-small" onClick={() => setRefresh((current) => current + 1)}>Muat semula</button>
      </div>

      {view === 'day' ? <div className="room-calendar-filters">
        <label className="field compact-field"><span>Cari bilik</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari bilik…" /></label>
        {places.length > 1 ? <label className="field compact-field"><span>Aras / bangunan</span><select value={place} onChange={(event) => setPlace(event.target.value)}><option value="">Semua aras</option>{places.map((value) => <option key={value} value={value}>{value}</option>)}</select></label> : null}
        <p className="calendar-result-count" role="status">{filteredRooms.length} {filteredRooms.length === 1 ? 'bilik' : 'bilik'}</p>
      </div> : <div className="room-calendar-filters week-filter">
        <label className="field compact-field"><span>Bilik mesyuarat</span><select value={selectedRoomId} onChange={(event) => setSelectedRoomId(event.target.value)}>{roomOptions.map((room) => <option key={room.room_id} value={room.room_id}>{room.name} · {roomPlace(room)}</option>)}</select></label>
      </div>}

      {status === 'loading' ? <div className="room-calendar-message"><Spinner label="Sedang memuatkan kalendar bilik…" /></div> : null}
      {status === 'error' ? <div className="room-calendar-message" role="alert"><p>{loadError || 'Kalendar bilik tidak dapat dimuatkan.'}</p><button type="button" className="button button-secondary button-small" onClick={() => setRefresh((current) => current + 1)}>Cuba lagi</button></div> : null}
      {status === 'ready' && view === 'day' && !filteredRooms.length ? <div className="room-calendar-message"><p>Tiada bilik yang sepadan dengan tapisan ini.</p></div> : null}
      {status === 'ready' && view === 'week' && !weekRoom ? <div className="room-calendar-message"><p>Tiada bilik mesyuarat aktif dikonfigurasikan.</p></div> : null}

      {status === 'ready' && ((view === 'day' && filteredRooms.length) || (view === 'week' && weekRoom)) ? <div className="room-calendar-scroll">
        <div className="room-calendar-grid">
          <TimeHeader lead={view === 'day' ? <><strong>{weekday(date, 'long')}</strong><span>{formatDate(date)}</span></> : <><strong>{weekRoom?.name}</strong><span>7:30 pagi–6:00 petang</span></>} />
          {view === 'day' ? filteredRooms.map((room) => <DayRoomRow
            room={room}
            date={date}
            key={room.room_id}
            onBookingClick={(booking, roomName) => setDetail({ booking, roomName })}
            onFreeClick={(roomId, start) => openCreateMeeting(roomId, start)}
          />) : Array.from({ length: 7 }, (_, index) => addIsoDays(date, index)).map((weekDate) => <WeekDayRow
            room={weekRoom!}
            date={weekDate}
            key={weekDate}
            onBookingClick={(booking, roomName) => setDetail({ booking, roomName })}
            onFreeClick={(roomId, start) => openCreateMeeting(roomId, start, weekDate)}
          />)}
        </div>
      </div> : null}
    </section>
    {detail ? <BookingDetailModal detail={detail} onClose={() => setDetail(null)} /> : null}
  </Layout>;
}
